import React, { useContext, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from 'components/ui/button'
import { StoreIOSAppUrl } from 'const'
import { ArrowLeft, Shield } from 'lucide-react'
import { logEvent } from 'firebase/analytics'
import { analytics } from 'lib/firebase'
import { AppContext } from 'context'

export default function DynamicIsland() {
	const { t } = useTranslation()
	const { authorized, user } = useContext(AppContext)
	const headerRef = useRef<HTMLElement>(null)
	const location = useLocation()
	const navigate = useNavigate()
	const isHome = location.pathname === '/'
	const isAdmin = authorized && user?.role === 'admin'

	useEffect(() => {
		let lastScrollY = window.scrollY
		let target = 0
		let current = 0
		let animationId: number

		function onScroll() {
			const currentY = window.scrollY
			const delta = currentY - lastScrollY
			lastScrollY = currentY
			target -= delta * 0.4
			target = Math.max(-14, Math.min(14, target))
		}

		function animate() {
			const el = headerRef.current
			if (el) {
				target *= 0.92
				const diff = target - current
				const speed = Math.abs(target) > Math.abs(current) ? 0.3 : 0.04
				current += diff * speed
				if (Math.abs(current) < 0.1) current = 0
				const squeeze = 1 - Math.abs(current) * 0.002
				el.style.transform = `translateY(${current}px) scaleY(${squeeze})`
			}
			animationId = requestAnimationFrame(animate)
		}

		window.addEventListener('scroll', onScroll, { passive: true })
		animationId = requestAnimationFrame(animate)
		return () => {
			window.removeEventListener('scroll', onScroll)
			cancelAnimationFrame(animationId)
		}
	}, [])

	if (isHome) return null

	return (
		<div className='fixed top-4 left-0 right-0 z-50 flex justify-center px-4'>
			<header
				ref={headerRef}
				className='flex items-center gap-3 px-2 h-11 rounded-full bg-background/95 backdrop-blur border border-border/40 supports-[backdrop-filter]:bg-background/60 will-change-transform'>
				{!isHome && (
					<button
						onClick={() => navigate(-1)}
						className='flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50 transition-colors text-zinc-900 dark:text-zinc-50'>
						<ArrowLeft className='w-4 h-4' />
					</button>
				)}
				<div className='flex items-center gap-2 text-zinc-900 dark:text-zinc-50 pl-2'>
					<img src='/image/icon_rounded.png' alt='logo' className='w-6 h-6 rounded-md' />
					{isHome && <span className='font-semibold text-sm'>{t('common.appName')}</span>}
				</div>
				{isAdmin && (
					<button
						onClick={() => navigate('/admin')}
						className='flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50 transition-colors text-zinc-900 dark:text-zinc-50'>
						<Shield className='w-4 h-4' />
					</button>
				)}
				{isHome ? (
					<Button
						size='sm'
						className='rounded-full px-5'
						onClick={() => {
							logEvent(analytics, 'store_click', { store: 'app_store', location: 'navbar' })
							window.location.href = StoreIOSAppUrl
						}}>
						{t('nav.download')}
					</Button>
				) : (
					<div className='pr-2' />
				)}
			</header>
		</div>
	)
}
